import type { RevealRow } from "@/hooks/use-reveal-flow";

/** One row of the tally: a prize type, how many of it the player just revealed, and its games. */
export interface TallyRow {
  /** React key — the raw title, unique per group by construction. Not the drop key (a collapsed
   *  row has several) and not the index (a stagger keyed on index re-targets on reorder). */
  key: string;
  /** The RAW server title. Format it at the point of display, never before grouping. */
  rawTitle: string;
  count: number;
  games: string[];
}

/**
 * Collapse the prizes of a settled reveal into one row per prize type, so winning the same thing
 * three times reads as "×3 · 50 Free Spins" rather than three identical lines. A tally, not a log.
 *
 * **Groups on the RAW title.** Grouping on the formatted one would be a real bug:
 * `stripFreespinGameName` collapses "100 Free Spins on Book of Dead" and "100 Free Spins on
 * Starburst" to the same string, so two genuinely different prizes — with different games — would
 * merge into one `×2` row.
 *
 * Order is first-disclosure order, i.e. the order the player's own claim staged them. Deliberately
 * not sorted by value: the only client-side value signal is a title-parsing heuristic, far too
 * weak to be ordering someone's money.
 */
export function collapseRevealRows(rows: RevealRow[]): TallyRow[] {
  const byTitle = new Map<string, TallyRow>();
  for (const row of rows) {
    const existing = byTitle.get(row.title);
    if (existing) {
      existing.count += 1;
      continue;
    }
    byTitle.set(row.title, {
      key: row.title,
      rawTitle: row.title,
      count: 1,
      games: row.games,
    });
  }
  return [...byTitle.values()];
}
